/**
 * Текст политики конфиденциальности. Источник — документ юриста
 * (privacy-policy-03dorixona.md, 30.09.2026); правки — только по новой версии
 * документа, вместе с датой обновления. Только по-русски: перевода нет.
 *
 * Хранится данными, а не разметкой: страница сама решает, как показать абзац,
 * список, важное замечание и контакты.
 */
export type Block =
  | { kind: 'p'; text: string }
  | { kind: 'list'; items: string[] }
  /** Пары «что — зачем»: собираемые данные. */
  | { kind: 'defs'; items: { term: string; text: string }[] }
  | { kind: 'note'; title: string; text: string }
  /** Реквизиты карточкой: какие поля показать — по документу раздела. */
  | { kind: 'contacts'; fields: ContactField[] }

export type ContactField = 'name' | 'company' | 'site' | 'email' | 'phone'

export interface Section {
  id: string
  title: string
  blocks: Block[]
}

export const CONTACTS = {
  company: '03 Dorixona',
  site: 'www.03dorixona.uz',
  siteUrl: 'https://www.03dorixona.uz',
  email: 'madinashifo@gmail.com',
  phone: '+998 50 100 99 03',
  phoneHref: 'tel:+998501009903',
}

export const PRIVACY = {
  title: 'Политика конфиденциальности',
  effective: '30 сентября 2026 г.',
  updated: '30 сентября 2026 г.',
  intro:
    'Настоящая Политика конфиденциальности описывает, какие персональные данные обрабатывает мобильное приложение «03 Dorixona» (далее — «Приложение»), с какой целью и на каких условиях. Используя Приложение, вы соглашаетесь с условиями настоящей Политики.',
  footer:
    '© 2026 03 Dorixona. Все права защищены. Настоящая Политика конфиденциальности регулируется законодательством Республики Узбекистан.',
}

export const SECTIONS: Section[] = [
  {
    id: 'general',
    title: 'Общие положения',
    blocks: [
      {
        kind: 'p',
        text: 'Приложение «03 Dorixona» — это электронная платформа для заказа и покупки лекарственных препаратов и товаров медицинского назначения с начислением кэшбэка за совершённые покупки.',
      },
      {
        kind: 'p',
        text: 'Обработка персональных данных осуществляется в соответствии с Законом Республики Узбекистан «О персональных данных» (№ ЗРУ-547) и иными применимыми нормативными актами.',
      },
    ],
  },
  {
    id: 'operator',
    title: 'Оператор персональных данных',
    blocks: [
      {
        kind: 'p',
        text: 'Оператором, определяющим цели и способы обработки персональных данных, является:',
      },
      { kind: 'contacts', fields: ['name', 'site', 'email'] },
    ],
  },
  {
    id: 'data',
    title: 'Какие данные мы собираем',
    blocks: [
      {
        kind: 'p',
        text: 'При регистрации и использовании Приложения мы запрашиваем и обрабатываем следующие данные:',
      },
      {
        kind: 'defs',
        items: [
          {
            term: 'Номер телефона',
            text: 'регистрация, вход в аккаунт, подтверждение через SMS-код (обязательно).',
          },
          {
            term: 'Имя и фамилия',
            text: 'идентификация пользователя, оформление заказов, обращение к клиенту (обязательно).',
          },
          { term: 'Пол', text: 'персонализация предложений и сервиса (обязательно).' },
          {
            term: 'Дата рождения',
            text: 'используется исключительно для поздравлений и подарков ко дню рождения (обязательно).',
          },
          {
            term: 'История заказов',
            text: 'оформление покупок и начисление кэшбэка (формируется автоматически).',
          },
        ],
      },
      {
        kind: 'note',
        title: 'Важно',
        text: 'Приложение не собирает медицинские диагнозы, результаты анализов или иные специальные категории медицинских данных. Обрабатывается только информация о совершённых заказах, необходимая для выполнения покупки и начисления кэшбэка.',
      },
    ],
  },
  {
    id: 'purposes',
    title: 'Цели обработки данных',
    blocks: [
      {
        kind: 'list',
        items: [
          'регистрация и аутентификация пользователя в Приложении;',
          'оформление, обработка и доставка заказов;',
          'начисление и учёт кэшбэка за покупки;',
          'поздравления и предоставление подарков ко дню рождения;',
          'информирование о статусе заказа и работе сервиса;',
          'обеспечение технической поддержки и безопасности.',
        ],
      },
    ],
  },
  {
    id: 'cashback',
    title: 'Кэшбэк и история покупок',
    blocks: [
      {
        kind: 'p',
        text: 'За приобретённые в Приложении товары пользователю начисляется кэшбэк. Для расчёта и учёта кэшбэка мы сохраняем историю заказов, включая наименование, количество и стоимость приобретённых товаров.',
      },
      {
        kind: 'p',
        text: 'Эти данные используются для корректного начисления вознаграждения и не передаются третьим лицам в маркетинговых целях без вашего согласия.',
      },
    ],
  },
  {
    id: 'grounds',
    title: 'Правовые основания обработки',
    blocks: [
      {
        kind: 'list',
        items: [
          'согласие пользователя, выраженное при регистрации в Приложении;',
          'исполнение договора (оформление и выполнение заказа);',
          'требования законодательства Республики Узбекистан.',
        ],
      },
    ],
  },
  {
    id: 'third-parties',
    title: 'Передача данных третьим лицам',
    blocks: [
      {
        kind: 'p',
        text: 'Мы можем передавать персональные данные только в объёме, необходимом для работы сервиса:',
      },
      {
        kind: 'list',
        items: [
          'службам доставки — для передачи заказа;',
          'платёжным провайдерам — для проведения оплаты;',
          'государственным органам — в случаях, предусмотренных законом.',
        ],
      },
      {
        kind: 'p',
        text: 'Мы не продаём персональные данные и не передаём их третьим лицам в целях, не связанных с работой Приложения.',
      },
    ],
  },
  {
    id: 'storage',
    title: 'Хранение и защита данных',
    blocks: [
      {
        kind: 'p',
        text: 'Персональные данные хранятся на защищённых серверах в течение срока, необходимого для достижения целей обработки, или до отзыва согласия пользователем.',
      },
      {
        kind: 'p',
        text: 'Для защиты данных применяются организационные и технические меры: шифрование при передаче, ограничение доступа и контроль за обработкой. При обработке данных граждан Республики Узбекистан их хранение обеспечивается на территории Республики Узбекистан в соответствии с требованиями законодательства.',
      },
    ],
  },
  {
    id: 'rights',
    title: 'Права пользователя',
    blocks: [
      { kind: 'p', text: 'Пользователь имеет право:' },
      {
        kind: 'list',
        items: [
          'получать информацию о своих обрабатываемых данных;',
          'требовать исправления неточных или неполных данных;',
          'отозвать согласие на обработку данных;',
          'требовать удаления учётной записи и связанных с ней данных;',
          'ограничить обработку данных в установленных законом случаях.',
        ],
      },
      {
        kind: 'p',
        text: 'Для реализации этих прав обратитесь к нам по контактным данным ниже. Удалить аккаунт также можно в настройках Приложения.',
      },
    ],
  },
  {
    id: 'technical',
    title: 'Технические данные и cookie',
    blocks: [
      {
        kind: 'p',
        text: 'Приложение может автоматически собирать технические данные, необходимые для его корректной работы: тип устройства, версия операционной системы, идентификатор сессии и файлы cookie в рамках встроенного веб-компонента (WebView). Эти данные используются для обеспечения работоспособности, безопасности и улучшения сервиса.',
      },
    ],
  },
  {
    id: 'minors',
    title: 'Данные несовершеннолетних',
    blocks: [
      {
        kind: 'p',
        text: 'Приложение не предназначено для лиц младше 18 лет. Мы не осуществляем целенаправленный сбор данных несовершеннолетних. Если такие данные были предоставлены, они будут удалены по обращению законного представителя.',
      },
    ],
  },
  {
    id: 'changes',
    title: 'Изменения политики',
    blocks: [
      {
        kind: 'p',
        text: 'Мы вправе вносить изменения в настоящую Политику. Актуальная версия всегда доступна в Приложении и по этому адресу. При существенных изменениях пользователи будут уведомлены через Приложение.',
      },
    ],
  },
  {
    id: 'contacts',
    title: 'Контактная информация',
    blocks: [{ kind: 'contacts', fields: ['company', 'site', 'email', 'phone'] }],
  },
]
